import { defineCollection } from "@rebasepro/cms-types";
import { SAY } from "../words";
import { policy } from "@rebasepro/types";
import type { FindParams, User } from "@rebasepro/types";

// One row per live game. The id is the 4-letter code people type on their phone,
// so creating a row is how a TV reserves a code nobody else is using.
// The game itself never touches this table: it runs on the TV and travels over
// the realtime channel `songbuzz:<code>`. This row is the reservation, plus a
// glanceable summary for the admin panel.
const MAX_ROOMS = 8;
const isAdmin = (u: User | undefined) => !!u && (u.uid === "service" || (Array.isArray(u.roles) && u.roles.includes("admin")));

const roomsCollection = defineCollection({
    name: "Rooms",
    singularName: "Room",
    slug: "rooms",
    table: "rooms",
    description: "Rooms opened in the last day. A TV opens one per night; it's deleted a day after the TV last updated it.",
    securityRules: [
        // Phones look a code up before joining.
        { operation: "select", access: "public" },
        // Any device signed in (guest sessions included) may open a room…
        { operation: "insert", condition: policy.authenticated() },
        // …and only the TV that opened it may change or close it.
        { operations: ["update", "delete"], ownerField: "owner" },
    ],
    callbacks: {
        // A phone looks a room up by its code, and nothing else: a list (or a count) shows only the caller's own
        // rooms, so nobody can collect every live code and walk into strangers' games. The admin panel sees them all.
        beforeQuery: ({ operation, context }) => {
            if (operation === "get" || isAdmin(context.user)) return;
            return { filter: { owner: ["==", context.user?.uid ?? ""] } };
        },
        // A TV opens a room a night (a phone hosting, one more), and rows go after a day without a game (the
        // close-stale-rooms cron). A guest holding more than MAX_ROOMS is a bot taking codes, so real TVs keep finding
        // free ones among the 160,000. Guest sign-ins are themselves limited per network by Rebase.
        beforeSave: async ({ status, values, context }) => {
            if (status !== "new" || isAdmin(context.user)) return values;
            const uid = context.user?.uid;
            if (!uid) throw new Error("Sign in to open a room");
            const mine = await context.data.rooms.count({ filter: { owner: ["==", uid] } } as FindParams);
            if (mine >= MAX_ROOMS) throw new Error("Too many rooms open on this device");
            return values;
        },
    },
    properties: {
        id: { name: "Code", type: "string", isId: "manual" },
        // the guest account the TV signed in as: only it may change or close the room
        owner: { name: "TV's sign-in", type: "string", autoValue: "user_on_create", admin: { readOnly: true, hideFromCollection: true } },
        phase: { name: "Where the game is", type: "string", admin: { Preview: "../../admin/src/previews/Words", customProps: { say: "phase" } } },
        players: { name: "Players", type: "number", validation: { integer: true } },
        source: { name: "Music", type: "string", admin: { Preview: "../../admin/src/previews/Words", customProps: { say: "mix", list: true } } },
        // the TV's public keys for this room (signing and encryption, JSON). Only the TV can write its row, so a phone
        // that reads them here knows which frames really come from the TV (src/core/roomwire.ts).
        keys: { name: "TV keys", type: "string", admin: { readOnly: true, hideFromCollection: true } },
        createdAt: { name: "Created", type: "date", autoValue: "on_create" },
        updatedAt: { name: "Updated", type: "date", autoValue: "on_update" },
    },
    admin: {
        icon: "Tv",
        group: "Games",
        openEntityMode: "side_panel",
        sort: ["updatedAt", "desc"],
        display: {
            // the code people type, what the room is playing, and where the game is
            title: "id",
            subtitle: ({ entity }) => entity.values.source || "Music not chosen yet",
            status: ({ entity }) => entity.values.phase ? SAY.phase(entity.values.phase) : undefined,
            date: "updatedAt",
        },
        listProperties: ["phase", "players", "source", "updatedAt"],
        form: {
            sections: [
                { key: "room", properties: ["id", "phase", "players", "source", "createdAt", "updatedAt"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["owner", "keys"] },
            ],
        },
    },
});

export default roomsCollection;
